import { useEffect, useState } from "react";
import { dismissModal } from "@/utils/modalClose";
import { createPortal } from "react-dom";
import {
  IonHeader, IonToolbar, IonTitle, IonButtons, IonButton, IonIcon, IonSpinner,
} from "@ionic/react";
import { closeOutline, openOutline } from "ionicons/icons";
import { userHeaders } from "@/utils/userAuth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
  catch { return iso || ""; }
};
const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const STATUS_BADGE = {
  paid: ["admin-badge-green", "Paid"],
  open: ["admin-badge-amber", "Open"],
  draft: ["admin-badge-slate", "Draft"],
  void: ["admin-badge-red", "Void"],
  uncollectible: ["admin-badge-red", "Uncollectible"],
};

// The account popover's Invoices: every subscription invoice, in the same
// portalled modal shell as Payment methods, listed like the admin calendar's
// purchases table. View opens Stripe's hosted invoice to read or download.
export default function InvoicesModal({ onClose: onCloseNow }) {
  const onClose = (e) => dismissModal(onCloseNow, e); // animated exit, then the real close

  const isMobile = window.innerWidth < 768;
  const [rows, setRows] = useState(null); // null while loading
  const [error, setError] = useState("");

  useEffect(() => {
    let on = true;
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/user/invoices`, { headers: userHeaders() });
        const d = await res.json().catch(() => ({}));
        if (!res.ok || d.success === false) throw new Error(d.detail || "Could not load your invoices");
        if (on) setRows(d.invoices || []);
      } catch (err) { if (on) { setError(err.message); setRows([]); } }
    })();
    return () => { on = false; };
  }, []);

  return createPortal(
    <div className="modal-backdrop invoices-modal" style={{ position: "fixed", inset: 0, zIndex: 10010, background: isMobile ? "var(--ion-background-color, #f2f2f7)" : "rgba(0,0,0,0.55)", display: "flex", alignItems: isMobile ? "stretch" : "center", justifyContent: isMobile ? "stretch" : "center" }}>
      <div className="modal-slide-up" style={{ background: "var(--ion-background-color, #f2f2f7)", color: "var(--ion-text-color)", display: "flex", flexDirection: "column", width: "100%", maxWidth: isMobile ? "100%" : 600, height: isMobile ? "100%" : "auto", maxHeight: isMobile ? "100%" : "90vh", overflow: "hidden" }}>
        <IonHeader>
          <IonToolbar style={{ "--background": "var(--ion-card-background)", "--color": "var(--ion-text-color)" }}>
            <IonButtons slot="start">
              <IonButton fill="clear" shape="round" onClick={onClose} style={{ "--color": "var(--ion-text-color)" }}>
                <IonIcon slot="icon-only" icon={closeOutline} style={{ fontSize: 26, color: "var(--ion-text-color)" }} />
              </IonButton>
            </IonButtons>
            <IonTitle style={{ fontWeight: 700 }}>Invoices</IonTitle>
          </IonToolbar>
        </IonHeader>
        <div style={{ flex: 1, overflowY: "auto", background: rows && rows.length > 0 ? "var(--ion-card-background, #ffffff)" : undefined }}>
          {rows === null ? (
            <div style={{ display: "flex", justifyContent: "center", padding: 40 }}><IonSpinner name="crescent" /></div>
          ) : rows.length === 0 ? (
            // the No-results look (size, weight, colour), icon-less, centred
            <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", padding: "24px 16px", color: "var(--ion-text-color)" }}>
              <div style={{ fontSize: "1.4rem", fontWeight: 700, textAlign: "center", lineHeight: 1.2, wordBreak: "break-word" }}>
                {error || "No invoices yet"}
              </div>
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <div>
                <table className="admin-table" style={{ width: "100%" }}>
                  <thead>
                    <tr><th>Date</th><th>Total</th><th>Status</th><th>Actions</th></tr>
                  </thead>
                  <tbody>
                    {rows.map((inv) => {
                      const [cls, label] = STATUS_BADGE[inv.status] || ["admin-badge-slate", inv.status || "-"];
                      return (
                        <tr key={inv.id}>
                          <td style={{ whiteSpace: "nowrap" }}>{fmtDate(inv.date)}</td>
                          <td>{money(inv.total)}</td>
                          <td><span className={`admin-badge ${cls}`}>{label}</span></td>
                          <td>
                            {inv.url ? (
                              <a href={inv.url} target="_blank" rel="noopener noreferrer"
                                style={{ display: "inline-flex", alignItems: "center", gap: 5, color: "var(--ion-color-primary)", fontWeight: 600, textDecoration: "none" }}>
                                View <IonIcon icon={openOutline} style={{ fontSize: 14 }} />
                              </a>
                            ) : <span style={{ color: "var(--ion-color-medium)" }}>-</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {rows !== null && rows.length > 0 && (
            <p style={{ margin: 0, padding: "10px 16px 14px", fontSize: "0.75rem", color: "var(--ion-color-medium)", background: "var(--ion-card-background, #ffffff)", textAlign: "center" }}>
              Subscription invoices and one-time purchases. View opens the Stripe receipt to read or download.
            </p>
          )}
        </div>
      </div>
    </div>,
    document.querySelector("ion-app") || document.body
  );
}
